import React, { useState } from 'react';
import { passwordAPI, authAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Lock, Eye, EyeOff, Save, User, Pencil, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import TwoFactorSettings from '../components/TwoFactorSettings';

const UserSettingsPage = () => {
  const { user, refreshUser } = useAuth();
  const [current, setCurrent] = useState('');
  const [newPass, setNewPass] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileName, setProfileName] = useState(user?.name || '');
  const [profileEmail, setProfileEmail] = useState(user?.email || '');
  const [savingProfile, setSavingProfile] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPass !== confirm) { toast.error('New passwords do not match'); return; }
    if (newPass.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setSaving(true);
    try {
      await passwordAPI.changePassword({ current_password: current, new_password: newPass });
      toast.success('Password changed successfully!');
      setCurrent(''); setNewPass(''); setConfirm('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally { setSaving(false); }
  };

  const handleSaveProfile = async () => {
    if (!profileName.trim()) { toast.error('Name cannot be empty'); return; }
    setSavingProfile(true);
    try {
      await authAPI.updateProfile({ name: profileName, email: profileEmail });
      toast.success('Profile updated!');
      setEditingProfile(false);
      if (refreshUser) refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally { setSavingProfile(false); }
  };

  return (
    <div className="space-y-6 animate-fade-in-up max-w-2xl">
      <div>
        <h1 className="text-2xl font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>Account Settings</h1>
        <p className="text-[#5F6368]">Manage your account and password</p>
      </div>

      {/* Profile Info */}
      <div className="dashboard-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-[#E8F0FE] rounded-full flex items-center justify-center">
              <User className="w-8 h-8 text-[#1A73E8]" />
            </div>
            {!editingProfile ? (
              <div>
                <h3 className="text-lg font-semibold text-[#202124]">{user?.name || ''}</h3>
                <p className="text-[#5F6368]">{user?.email || ''}</p>
                <span className="inline-block mt-1 text-xs bg-[#E8F0FE] text-[#1A73E8] px-2 py-0.5 rounded-full font-medium capitalize">
                  {user?.membership_plan || 'free'} Plan
                </span>
              </div>
            ) : (
              <div className="space-y-2 flex-1">
                <input value={profileName} onChange={e => setProfileName(e.target.value)} className="input-google" placeholder="Your Name" data-testid="user-profile-name" />
                <input value={profileEmail} onChange={e => setProfileEmail(e.target.value)} className="input-google" placeholder="Email" data-testid="user-profile-email" />
              </div>
            )}
          </div>
          {!editingProfile ? (
            <button onClick={() => { setEditingProfile(true); setProfileName(user?.name || ''); setProfileEmail(user?.email || ''); }} className="flex items-center gap-1 text-sm text-blue-600 hover:underline" data-testid="edit-profile-btn">
              <Pencil className="w-4 h-4" /> Edit
            </button>
          ) : (
            <div className="flex gap-2">
              <button onClick={() => setEditingProfile(false)} className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50">Cancel</button>
              <button onClick={handleSaveProfile} disabled={savingProfile} className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50" data-testid="save-profile-btn">
                {savingProfile ? 'Saving...' : 'Save'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Change Password */}
      <div className="dashboard-card">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-[#FEF7E0] rounded-lg flex items-center justify-center">
            <Lock className="w-5 h-5 text-[#F9AB00]" />
          </div>
          <div>
            <h3 className="font-semibold text-[#202124]">Change Password</h3>
            <p className="text-sm text-[#5F6368]">Update your account password</p>
          </div>
        </div>
        <form onSubmit={handleChangePassword} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#5F6368] mb-1">Current Password</label>
            <div className="relative">
              <input type={showCurrent ? "text" : "password"} value={current} onChange={(e) => setCurrent(e.target.value)}
                className="input-google pr-10" required data-testid="user-current-password" />
              <button type="button" onClick={() => setShowCurrent(!showCurrent)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5F6368]">
                {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#5F6368] mb-1">New Password</label>
            <div className="relative">
              <input type={showNew ? "text" : "password"} value={newPass} onChange={(e) => setNewPass(e.target.value)}
                className="input-google pr-10" required minLength={6} data-testid="user-new-password" />
              <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5F6368]">
                {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#5F6368] mb-1">Confirm New Password</label>
            <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
              className="input-google" required minLength={6} data-testid="user-confirm-password" />
          </div>
          <button type="submit" disabled={saving} className="btn-google flex items-center gap-2" data-testid="user-save-password-btn">
            {saving ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
            {saving ? 'Saving...' : 'Update Password'}
          </button>
        </form>
      </div>

      {/* Two-Factor Authentication */}
      <TwoFactorSettings />
    </div>
  );
};

export default UserSettingsPage;
